/**
 * ProtectedRoute — Guards routes that require authentication.
 *
 * Renders children only when the user is authenticated.
 * During auth loading, shows a spinner. If unauthenticated,
 * redirects to /auth with a return-to query param.
 */

import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

const ProtectedRoute = ({ children }: ProtectedRouteProps) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  // Show loading spinner while auth is resolving
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-ds-bg">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-4 border-ds-red border-t-transparent rounded-full animate-spin" />
          <p className="text-ds-text-secondary text-sm">Loading...</p>
        </div>
      </div>
    );
  }

  // Not authenticated — redirect to auth with return-to param
  if (!user) {
    return <Navigate to={`/auth?returnTo=${encodeURIComponent(location.pathname)}`} replace />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
